<script setup lang="ts">
/**
 * 全局系统异常提示横幅（SystemAlertBanner）
 *
 * 作用：当后端服务不可达 / 网络异常 / 服务端 5xx 错误时，在页面顶部展示统一的降级提示。
 * 数据来源：useSystemErrorStore.globalError
 *
 * 特点：
 * - 非侵入式：只在需要时渲染，不影响现有页面布局
 * - 分类化展示：网络/超时/服务器错误各有不同图标和配色
 * - 可关闭：用户点击关闭后隐藏
 * - 自动清除：成功请求后自动清除错误状态
 */
import { computed } from 'vue'
import { useSystemErrorStore } from '@/stores/systemError'
import {
  WifiOff,
  Clock4,
  ServerCrash,
  AlertTriangle,
  RefreshCw,
  X,
} from 'lucide-vue-next'

const sysErr = useSystemErrorStore()

// ============ 视觉映射 ============

const visual = computed(() => {
  const cat = sysErr.errorCategory
  switch (cat) {
    case 'network':
      return {
        icon: WifiOff,
        bg: 'from-amber-50 to-orange-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
      }
    case 'timeout':
      return {
        icon: Clock4,
        bg: 'from-amber-50 to-yellow-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
      }
    case 'server':
      return {
        icon: ServerCrash,
        bg: 'from-rose-50 to-red-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100',
        iconColor: 'text-rose-600',
      }
    default:
      return {
        icon: AlertTriangle,
        bg: 'from-slate-50 to-gray-50',
        border: 'border-slate-200',
        iconBg: 'bg-slate-100',
        iconColor: 'text-slate-600',
      }
  }
})

// ============ 事件处理 ============

function dismiss(): void {
  sysErr.clear()
}

function reload(): void {
  window.location.reload()
}
</script>

<template>
  <!-- 仅在存在全局错误时渲染 -->
  <Teleport to="body">
    <div
      v-if="sysErr.hasError"
      class="fixed left-0 top-0 z-[9999] w-full"
      role="alert"
      aria-live="assertive"
    >
      <!-- 顶部彩色条 -->
      <div
        class="h-0.5 w-full bg-gradient-to-r from-transparent via-current to-transparent opacity-60"
        :class="visual.iconColor"
      />

      <!-- 横幅容器 -->
      <div
        class="flex items-center gap-3 border-b bg-gradient-to-r px-4 py-2.5 shadow-lg md:px-6"
        :class="[visual.bg, visual.border]"
      >
        <!-- 图标 -->
        <div
          class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
          :class="visual.iconBg"
        >
          <component :is="visual.icon" :size="16" :class="visual.iconColor" />
        </div>

        <!-- 文案 -->
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-[var(--color-title)]">
            {{ sysErr.globalError?.message || '系统异常' }}
          </p>
        </div>

        <!-- 操作按钮 -->
        <div class="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            class="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-[var(--color-body)] transition-colors hover:bg-white/60"
            @click="reload"
          >
            <RefreshCw :size="14" />
            刷新
          </button>
          <button
            type="button"
            class="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--color-caption)] transition-colors hover:bg-white/60"
            @click="dismiss"
          >
            <X :size="16" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
